"use client";

import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { href: '#dlaczego-my', label: 'Dlaczego my' },
  { href: '#jak-dzialamy', label: 'Jak działamy' },
  { href: '#faq', label: 'FAQ' },
  { href: '#kontakt', label: 'Kontakt' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = React.useState('');

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-80px 0px -40% 0px' }
    );

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      const offset = 80;
      const elementPosition = target.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-black border-b border-zinc-800">
      <div className="max-w-screen-2xl mx-auto">
        <div className="flex items-center justify-between px-8 h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-x-3 group" onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}>
            <div className="w-10 h-10 bg-blue-600 rounded-2xl flex items-center justify-center group-hover:bg-blue-500 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <span className="text-3xl font-semibold tracking-tighter">DaHaka</span>
              <span className="text-3xl font-semibold tracking-tighter text-blue-500">Inc.</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-x-9 text-sm font-medium">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`nav-link px-3 py-2 text-zinc-300 hover:text-white ${activeSection === link.href.slice(1) ? 'active' : ''}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="flex items-center gap-x-3">
            <a
              href="#kontakt"
              onClick={(e) => scrollToSection(e, '#kontakt')}
              className="hidden sm:flex items-center px-5 py-2.5 bg-white hover:bg-zinc-100 active:bg-zinc-200 text-zinc-950 font-semibold text-sm rounded-2xl transition-all"
            >
              Zapytaj o ofertę
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-2xl transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden border-t border-zinc-800 bg-zinc-950">
          <div className="px-8 py-6 flex flex-col gap-y-1 text-sm font-medium">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="px-4 py-3 text-zinc-300 hover:bg-zinc-900 rounded-2xl"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4">
              <a
                href="#kontakt"
                onClick={(e) => scrollToSection(e, '#kontakt')}
                className="flex w-full justify-center items-center px-5 py-3 bg-white text-zinc-950 font-semibold rounded-2xl"
              >
                Zapytaj o ofertę
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
